import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { computeRmssdFromNn } from '../../../shared/hrv-preprocessing/computeRmssdFromNn';
import { deriveNnFromHr } from '../../../shared/hrv-preprocessing/deriveNnFromHr';
import type {
  CustomTimeSegment,
  SegmentHrvMetrics,
  SessionHrvMetricsRow,
  SpectralWorkspaceSession,
} from '../types';
import { filterEventsByCustomSegment } from './customSegment';

export const eventsToRrIntervals = (events: HeartRateEvent[]) => {
  const sorted = [...events].sort(
    (left, right) => left.timestamp - right.timestamp
  );

  return deriveNnFromHr(
    sorted.map(event => event.hr),
    sorted.map(event => event.timestamp)
  );
};

export const computeSegmentHrvMetricsFromEvents = (
  events: HeartRateEvent[]
): SegmentHrvMetrics | null => {
  const intervals = eventsToRrIntervals(events);

  if (intervals.length === 0) {
    return null;
  }

  const meanRrMs =
    intervals.reduce((sum, interval) => sum + interval.rrMs, 0) /
    intervals.length;

  const rmssdMs = intervals.length > 1 ? computeRmssdFromNn(intervals) : null;

  return {
    meanRrMs,
    rmssdMs,
    beatCount: intervals.length,
  };
};

export const computeSessionHrvMetrics = (
  session: Pick<
    SpectralWorkspaceSession,
    'sessionId' | 'patientCode' | 'events'
  >,
  customSegments: CustomTimeSegment[] = []
): SessionHrvMetricsRow => {
  const events = session.events ?? [];

  const custom = Object.fromEntries(
    customSegments.map(segment => [
      segment.id,
      computeSegmentHrvMetricsFromEvents(
        filterEventsByCustomSegment(
          events,
          segment.startSecond,
          segment.endSecond
        )
      ),
    ])
  );

  return {
    sessionId: session.sessionId,
    patientCode: session.patientCode,
    full: computeSegmentHrvMetricsFromEvents(events),
    custom,
  };
};
